import React, { useState } from "react"
import { Card, Input, Button, List, message, Select, Popconfirm } from "antd"
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { secureApi } from "../../Config/api"
import { POST } from "../../utils/apis"
import { useDispatch, useSelector } from "react-redux"
import { fetchEstimations, fetchStats } from "../../utils/helpers"
import { translations } from "../../utils/constants"
import ExcelUploadButton from "../ExcelUploadButton/ExcelUploadButton"

const unitValues = [
    { name: "cavo lan (MT)", price: 2.10 },
    { name: "CAVO 4X0,50", price: 0.61 },
    { name: "CAVO 2X0,75", price: 0.50 },
    { name: "CAVO 1X1,5 ROSSO", price: 0.28 },
    { name: "CAVO 1X1,5 NERO", price: 0.28 },
    { name: "CAVO 1X1,5ARANCIO", price: 0.28 },
    { name: "N°M12", price: 17.70 },
    { name: "N° M12 8 POOLI", price: 27.50 },
    { name: "rj45", price: 7.50 },
    { name: "morsettiera wago fusibilata +barra din", price: 1.00 },
    { name: "puntalini", price: 0.10 },
    { name: "RELAY", price: 5.00 },
    { name: "RELAY TEMPORIZZATO", price: 24.00 },
    { name: "faston maschio maschio", price: 0.04 },
    { name: "faston femmina", price: 0.04 },
    { name: "CONNETTORE MASCHIO 3 POLI", price: 0.59 },
    { name: "CONNETTORE FEMMINA 3 POLI", price: 0.57 },
    { name: "CONNETTORE MASCHIO 2 POLI", price: 0.39 },
    { name: "CONNETTORE FEMMINA 2 POLI", price: 0.37 },
    { name: "CONNETTORE MASCHIO 1 POLI", price: 0.16 },
    { name: "CONNETTORE FEMMINA 1 POLI2", price: 0.14 },
    { name: "CONNETTORE ALIMENTAZIONE ENOBU", price: 15.00 },
    { name: "CAVO ALIMENTAZIONE ENOBU (ROSSO-NERO-ARANCIO)", price: 0.84 },
    { name: "CONNETTORE ALIMENTAZIONE SWITCH", price: 18.62 },
    { name: "CAVO ALIMENTAZIONE SWITCH", price: 1.00 },
    { name: "CONNETTORE SEGNALI ENOBU", price: 25.00 },
    { name: "CAVO SEGNALI ENOBU", price: 1.00 },
    { name: "STAFFE CARTELLI INDICATORI", price: 40.00 },
    { name: "STAFFE LCD", price: 160.00 },
    { name: "STAFFE LCD 2", price: 77.00 },
    { name: "SCATOLE CONTAPASSEGGERI", price: 10.00 },
    { name: "STAFFA RINFORZO ENOBU", price: 5.00 },
]

const UnitManagement = (props) => {
    const { vehicleId, language } = props
    const user = useSelector((state) => state.authReducer?.user)
    const dispatch = useDispatch()
    const estimationData = useSelector((state) => state.estimation.data) || []
    const [newComponent, setNewComponent] = useState({ name: "", unitPrice: 0 })
    const [loading, setLoading] = useState(false)

    const uniqueUnits = React.useMemo(() => {
        const seen = new Set()
        return estimationData.filter(item => {
            if (seen.has(item.unit_id)) return false
            seen.add(item.unit_id)
            return true
        })
    }, [estimationData])

    // console.log('user', user)
    const handleAdd = async () => {
        if (!newComponent.name) {
            return message.error(translations[language]?.pleaseSelectUnit)
        }
        try {
            setLoading(true)
            const payload = {
                vehicle_id: vehicleId,
                row_name: newComponent.name,
                unit_price: newComponent.unitPrice,
            }
            const res = await secureApi.post(POST.ESTIMATION_ADD_ROW, payload)
            if (res?.success) {
                setNewComponent({ name: "", unitPrice: 0 })
                message.success(translations[language]?.unitAdded)
                fetchStats({ dispatch, user, vehicleId })
                fetchEstimations({ user, vehicleId, dispatch })
            } else {
                message.error(res?.message || translations[language]?.unitAddError)
            }
        } catch (err) {
            message.error(translations[language]?.unitAddError)
        } finally {
            setLoading(false)
        }
    }

    const handlePriceChange = async (id, value) => {
        try {
            const updatedPrice = parseFloat(value) || 0
            await secureApi?.post(POST?.UPDATE_UNIT_PRICE, {
                vehicle_id: vehicleId,
                unit_id: id,
                new_price: updatedPrice
            })
            fetchStats({ dispatch, user, vehicleId })
            fetchEstimations({ user, vehicleId, dispatch })
        } catch (err) {
            message.error(translations[language]?.updatePriceError || "Failed to update unit price")
        }
    }

    const handleDelete = async (id) => {
        try {
            const res = await secureApi?.post(POST.DELETE_UNIT, {
                vehicle_id: vehicleId,
                unit_id: id
            })
            if (res?.success) {
                message.success(translations[language]?.unitDeleted)
                fetchEstimations({ user, vehicleId, dispatch })
                fetchStats({ dispatch, user, vehicleId })
            } else {
                message.error(res?.message || translations[language]?.unitDeleteError)
            }
        } catch (err) {
            message.error(translations[language]?.unitDeleteError)
        }
    }

    return (
        <div className="components-tab">
            <Card className="section-card" bordered={false}>
                <h3>{translations[language]?.addUnit}</h3>
                <p>{translations[language]?.addUnitSubtitle}</p>
                <div style={{ display: "flex", gap: "10px", marginTop: "12px" }} className="unit-input-group">
                    <Select
                        showSearch
                        placeholder={translations[language]?.selectUnit}
                        style={{ width: "100%" }}
                        options={unitValues.map((v) => ({
                            label: v.name,
                            value: v.name,
                            price: v.price,
                        }))}
                        onChange={(value, option) =>
                            setNewComponent({ name: value, unitPrice: option.price })
                        }
                        value={newComponent.name || undefined}
                    />
                    <Input
                        type="number"
                        prefix="€"
                        style={{ borderRadius: 6 }}
                        min="0"
                        placeholder={translations[language]?.unitPrice}
                        value={newComponent.unitPrice}
                        onChange={(e) =>
                            setNewComponent({
                                ...newComponent,
                                unitPrice: parseFloat(e.target.value) || 0,
                            })
                        }
                    />
                    <Button
                        className="dashboard-export"
                        type="primary"
                        loading={loading}
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                    >
                        {translations[language]?.addUnit}
                    </Button>
                </div>
                {user?.id && user?.role === 'admin' &&
                    <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
                        <ExcelUploadButton
                            user={user}
                            {...props}
                        />
                    </div>
                }
            </Card>

            <Card className="section-card" bordered={false} style={{ marginTop: 20 }}>
                <h3>{translations[language]?.unitsLibrary}</h3>
                <p>{translations[language]?.unitsLibrarySubtitle}</p>
                <div className="unit-lists-div">
                    <List
                        dataSource={uniqueUnits}
                        rowKey={(comp) => comp.unit_id}
                        renderItem={(comp) => (
                            <List.Item
                                key={comp.unit_id}
                                style={{
                                    borderRadius: "10px",
                                    background: "#f5faff",
                                    marginBottom: "10px",
                                    padding: "12px 16px",
                                }}
                                actions={[
                                    <Popconfirm
                                        title={translations[language]?.deleteMsg}
                                        okText={translations[language]?.yes}
                                        cancelText={translations[language]?.no}
                                        onConfirm={() => handleDelete(comp.unit_id)}
                                    >
                                        <DeleteOutlined
                                            key="delete"
                                            // onClick={() => handleDelete(comp.unit_id)}
                                            style={{
                                                color: "red",
                                                fontSize: "18px",
                                                cursor: "pointer",
                                            }}
                                        />
                                    </Popconfirm>
                                    ,
                                ]}
                            >
                                <div>
                                    <strong>{comp.unit_name}</strong>
                                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                        <Input
                                            min="0"
                                            type="number"
                                            prefix="€"
                                            step={0.1}
                                            value={comp.unit_price}
                                            onChange={(e) =>
                                                handlePriceChange(comp.unit_id, e.target.value)
                                            }
                                            style={{ width: "100px" }}
                                        />
                                        <span>{translations[language]?.perUnit}</span>
                                    </div>
                                </div>
                            </List.Item>
                        )}
                    />
                </div>
            </Card>
        </div>
    )
}

export default UnitManagement